import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence, type PanInfo } from 'framer-motion'
import {
  ChevronLeft,
  ChevronRight,
  Compass,
  Gem,
  HeartHandshake,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { LOGO_PATH, LOGO_ALT } from '@/lib/brand'
import { StoreImage } from '@/store/components/StoreImage'
import { cn } from '@/lib/utils'

const STORAGE_KEY = 'bazm-welcome-onboarding-seen'

type Slide = {
  icon: LucideIcon
  title: string
  body: string
  footnote?: string
}

const SLIDES: Slide[] = [
  {
    icon: HeartHandshake,
    title: 'Welcome to Bazm-e-Musk',
    body:
      'Thank you for visiting our website — we are glad to have you here. As this is your first time with us, please allow us a few moments to share why you should choose us and what makes us different.',
  },
  {
    icon: Gem,
    title: 'Quality You Can Trust',
    body:
      'We make sure to ship only the best quality attars and fragrances. Every product reflects our commitment to purity, longevity, and the heritage of oriental perfumery.',
  },
  {
    icon: RotateCcw,
    title: 'Returns Made Fair',
    body:
      'We fully understand if a fragrance is not the right fit for you. If you did not like your product, you may return it to us — shipping costs may apply depending on your address. Terms & conditions apply.',
    footnote: 'See our Return & Refund Policy for full details.',
  },
  {
    icon: Sparkles,
    title: 'Crafted to Stand Apart',
    body:
      'From rare oud and musk to floral essences, our collection is curated for those who appreciate luxury without compromise. Discover attars that leave a lasting impression.',
  },
  {
    icon: Compass,
    title: 'Explore Our Website',
    body:
      'Track orders with your email and phone, search our full attar catalogue, request products we do not yet list, save favourites to your wishlist, and checkout securely with premium packaging — all in one seamless experience.',
  },
  {
    icon: ShieldCheck,
    title: 'We Are Here for You',
    body:
      'Secure checkout, order tracking, and friendly support whenever you need us. Reach out on WhatsApp or through our contact page — your satisfaction is at the heart of everything we do.',
  },
]

function hasSeenWelcomeOnboarding(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function markWelcomeOnboardingSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, '1')
  } catch {
    /* ignore */
  }
}

export function WelcomeOnboardingModal() {
  const [open, setOpen] = useState(() => !hasSeenWelcomeOnboarding())
  const [slide, setSlide] = useState(0)

  const close = useCallback(() => {
    markWelcomeOnboardingSeen()
    setOpen(false)
  }, [])

  const next = () => {
    if (slide < SLIDES.length - 1) {
      setSlide((s) => s + 1)
    } else {
      close()
    }
  }

  const prev = () => setSlide((s) => Math.max(0, s - 1))

  const handleSwipeEnd = (_: unknown, info: PanInfo) => {
    const threshold = 48
    if (info.offset.x <= -threshold) {
      next()
    } else if (info.offset.x >= threshold) {
      prev()
    }
  }

  if (!open) return null

  const current = SLIDES[slide]
  const Icon = current.icon
  const isLast = slide === SLIDES.length - 1

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px] pointer-events-auto"
        aria-hidden
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', damping: 26, stiffness: 280 }}
        className="relative w-full max-w-lg pointer-events-auto overflow-hidden rounded-2xl border border-gold-500/30 bg-luxury-950/95 shadow-[0_0_60px_rgba(212,175,55,0.15)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-slide-title"
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent opacity-80" />
        <div className="arabesque-pattern absolute inset-0 opacity-[0.07] pointer-events-none" />

        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 z-10 rounded-lg p-2 text-zinc-400 hover:bg-luxury-800 hover:text-zinc-100"
          aria-label="Close welcome guide"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="relative px-6 pt-8 pb-6 sm:px-8 sm:pt-10">
          {slide === 0 && (
            <div className="mb-6 flex justify-center">
              <StoreImage
                src={LOGO_PATH}
                alt={LOGO_ALT}
                priority
                objectFit="contain"
                spinnerSize="sm"
                containerClassName="h-16 w-16 shrink-0 overflow-hidden rounded-full"
                className="h-16 w-16 object-contain drop-shadow-[0_0_20px_rgba(212,175,55,0.35)]"
              />
            </div>
          )}

          <p className="mb-4 text-center text-[10px] uppercase tracking-[0.28em] text-zinc-500">
            Swipe left or right
          </p>

          <AnimatePresence mode="wait">
            <motion.div
              key={slide}
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -28 }}
              transition={{ duration: 0.28 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.12}
              onDragEnd={handleSwipeEnd}
              className="cursor-grab touch-pan-y text-center active:cursor-grabbing"
            >
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-gold-500/25 bg-gold-500/10">
                <Icon className="h-7 w-7 text-gold-400" />
              </div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-gold-500/90 mb-2">
                {slide + 1} / {SLIDES.length}
              </p>
              <h2
                id="welcome-slide-title"
                className="font-display text-2xl font-bold gold-gradient-text sm:text-3xl"
              >
                {current.title}
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-zinc-300 sm:text-base">
                {current.body}
              </p>
              {current.footnote && (
                <p className="mt-3 text-xs text-zinc-500">
                  {current.footnote}{' '}
                  <Link
                    to="/return-refund-policy"
                    onClick={close}
                    className="text-gold-400 underline underline-offset-2 hover:text-gold-300"
                  >
                    Read policy
                  </Link>
                </p>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex justify-center gap-2">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSlide(i)}
                className={cn(
                  'h-2 rounded-full transition-all',
                  i === slide
                    ? 'w-8 bg-gold-400'
                    : 'w-2 bg-luxury-600 hover:bg-luxury-500'
                )}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={close}
              className="text-sm text-zinc-500 hover:text-zinc-300"
            >
              Skip for now
            </button>
            <div className="flex gap-2">
              {slide > 0 && (
                <Button type="button" variant="secondary" size="sm" onClick={prev}>
                  <ChevronLeft className="h-4 w-4" /> Back
                </Button>
              )}
              <Button type="button" size="sm" onClick={next}>
                {isLast ? (
                  'Start exploring'
                ) : (
                  <>
                    Next <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
